import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchAllThreatActors as getAllThreatActorsService, deleteThreatActor as deleteThreatActorService } from 'services/modules/ecs/data';
import { useNotification } from 'config/Notification';

import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  TablePagination,
  Divider,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment
} from '@mui/material';

const ViewThreatActors = () => {
  // State variables
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [threatactors, setThreatactors] = useState([]);
  const [total, setTotal] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threatactorToDelete, setThreatactorToDelete] = useState({ name: '', id: '' });

  // React router
  const navigate = useNavigate();

  // Notification
  const { openNotification } = useNotification();

  // Method to fetch companies data
  const fetchThreatActorsData = async () => {
    try {
      setLoadingData(true);
      const params = { page: page + 1, limit: rowsPerPage};
      const response = await getAllThreatActorsService({ params});
      if(response){
        setThreatactors(response.data);
        setTotal(response.pagination.total);
      }
    } catch (error) {
      setLoadingData(false);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchThreatActorsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[page, rowsPerPage])

  // Handles page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handles change in search query like company name,domain or location
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handles navigation to company profile component
  const handleViewCompany = async (company) => {
    navigate(`/tip/view-threatactor?threat=${company.id}`);
  };

  // Handles navigation to edit company component
  const handleEditCompany = (companyId) => {
    navigate(`/tip/edit-threatactor?threat=${companyId}`);
  };

  // This method is called when user clicks on delete threatactor under actions
  const handleDeleteClick = (name, id) => {
    setThreatactorToDelete({ name, id });
    setDeleteDialogOpen(true);
  };

   // Method to handle threatactor deletion
   const handleDeleteConfirm = async (id) => {
    try {
      let deleteStatus = await deleteThreatActorService(id);
      setDeleteDialogOpen(false);
      if (deleteStatus) {
        openNotification('Company deleted successfully', 'success');
        fetchThreatActorsData();
      } else {
        openNotification('Error while deleting the company', 'error');
      }
    } catch (error) {
      openNotification('Error while deleting the company', 'error');
    }
  };

  // Handles navigation to add company component based on permissions
  const handleAddCompany = () => {
    navigate(`/tip/add-threatactor`);
  };

  return (
    <>
      <div className="m-2">
        <div className="row header-cont p-2">
          <div className="col-xs-12 col-sm-12 col-md-6 col-lg-3 partner-cont">
            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADwAAAA8CAYAAAA6/NlyAAAACXBIWXMAAAsTAAALEwEAmpwYAAAHKElEQVR4nNWbedBWcxTHL6Io2YpoEFOWigzKGMvIiEJClj9SmsgSBlmSKfsyCcVgTFS2aJDGUpaGQULLjCUalWXsbbLUS4v6mOP53rfjus9z732293m/M8+8z+/ec87vd577u2f7nTcIqgBgB2AI8ALwNVAHrAa+AKYC5wOtgsYOcoreLwWTYDT3AK2DxgjgFGCplNkIvAIMAjoAzYGtgfbAQOA9p7jxnBQ0FgCbAbdIScObQMcUfF2AD9wPdFPQSJS9T4teD1xu1zLwbwEMB/6WjPuy8FcdwK1a6F9ArxLknA2sk6wbgloE0E9bcV053kHgND1pk3l2UEsA9gb+0BO5pIxyh0nmKjNwQS0A2NxZ2WcqYBPMTxveqYn3GThPC1peCR8K7CjZhgHllp8JwLbAMi2mfwXnuUBz/GA+vFLzJEIuxDDXtnZQ2ddmnua6plLzFIQipXCrnZiCvi1wDHAccCpwpqKxg4GdU/Cf6p7yVkG1QS5MNHwcNSZAM6AnMFbG5pcUsbT57pnmd4HDgSYxT3mBaM9tCIVna/Ih7loHJQsrYxT6HpgFvApMBp4GXgTeBX6KoV8B3OvdkbIqw9vVVra9JrYgozWwK/C4CwkNi4HbgR5pUj8ZwOOBO4DPnBwLUScAbYDttBMsGNmrIYzVdOAE4DeNNwBP2HuZwL+TKZhAY+/2oy7E/NW2MjBF46vLrlg+uECjPzBR398COifwddS2RrvhbWD/FFFcGHwYvtTfN4NqAGihX32jntRjWsDABL42ei/DZH+tvpsf3yXFvAMihYS1VfHJ5NzKv++oxmkVHiO6V2TFW8iAGW5LOfeR2tohjiqXXnlhjl+TTcqo8Gui6+Gunaxr0wrxRuQcI0NmuDKoNMgZJcPlGRU+Vha4PmiQCzM8knENVlExTChBlXSQ3zR013hiGoVjgogxzngdloLnMHsV9L1H1QwX8I0m61CCwkPFsyYNn9v6ozXeJ7TYJaqTDOdzW2o8XuNBGWSEruWMjGHseJcyGpYHlQab3ElTjUdrPDTjj/Z7NF4uQH+V5hij8TYary5BlXTQiUFdmDA4qz02pQjjOcA+Gejv96mhGb7wlQgqDXK+8Ag37q7JZ2eQ0cu7pxT0c71Ls3RS46VBA+XFa2Rt90jJY9t5WUradorR68LIysJRKbwwaAgAT2kBt6ekP9Eyo5S0d0r25JhiwPSgIQB00wLq0j7llHL3dPHzoe76zd5NlR3k4l5Lxj8BvtLHzoF2cDSTQlfhaEr9hCWkDZHrYQ18hWpdI4Ety6nwDP6PP+0JOJrWeSoX1cK4cim7nwT+bNtK+ene/uk62ubufrU+3VUFsV2wTTkUPlcKvxXUKNRVYGhXqqB9XSj5WVCjcIatRSlCmqsHI8SKoAah4l/pYSbwsAQtcCWd6hfAE6DSsOGrUoQcLwXXy8datd+we0Y5FiV1ykDfKet7aGUerW1WFr56WBYELPKRkzvX6RpkgNJAy676uKThLpV3pynT6qJ7fUS7OOMcdmRjmJKFL67mPN+lgFZ4M/QOimuBMEWedY0uHhtVaw7TzlsyznGp+B6MXL8yMUEBtnfHJPXKuST//CyLEe8op5wlGOOAs9THMS5yWjGqCPm3iXeku7abrq0MCxX5mG8S4Wx/QKYjE8OIEsq6llEdnSfdtBwbn3ZmkG+nE4YLItftYMAwPB9jM3e43TNy7zJdf6CIBT0n3htTvEaTipAfvm6nxFRJkU7NCtWN5hUwDM8XsaDPxXtgQoBjmF+E/NCgdou5N0f3+hUqv15awPTPLGJBP4q3bcJhueG7IuTbMSxxaSmbWiX+GxYbsazl2rijTVcWzeQyxPu6ePsUoOlbTEKvLp8wKGqaJwpbrfubYgjgCk04NY/glrq/KsuCIm7jo7h3SRVIy7MNF2WU3Up8K1PYkIv9xfBQ65xyB+iWmLs69Id2iuju7eoaShelLds6/s7iXZAi25sWuJKnJfMUCutUaSBrR5yLoEI87e49467XR2RFuLy8qSu5CudGbe0mduEQMX2fIPz9rL4youyjbpfMd20Ndc6Xro26lxT9nYkdgOTaLwxdbHBhtCqYh8na9w19MyzoWx9BqVq5xD3VJWHXrYvIUhvG6IlEiqrqYH9Ucm0C00NZm0dVTj0vJsA5VDurWUwbY2LPl6MP1z4sZZPqqNSHYeqfMtwa1AiAJ9P0bQGDRTfOBndrcH0C04XFHF5XEsAbcaFwDN1I0d1pg9M1MF+4RYIBMrwU1AiAT+uNUX6aJi687R0m/KHLeSJfZ4xO4A1zghqBS3Zi25bJRVqh61tYX6KSMquc5bxHFrVJJKrZIJ+d+F8qlYbq0aF/3UyfnVUmslz7EXdyseJ/1Ro1jVlzp8d1EZqXaHyYEbZn5Pvluuqw6uXor6JWA+vpiGsebWhY0GJb3IIMW/sI4KCogv8AOqy7P7bM64cAAAAASUVORK5CYII=" className="card-icon" style={{background: '#0e0a65'}} alt="logo"></img>
            <span className="icon-color text-h5 text-bold font">ThreatActors</span>
          </div>
          <div className="col-xs-12 col-sm-6 col-md-6 col lg-3 cont">
            <Button variant="contained" className="add-company font" onClick={() => handleAddCompany()}>Add ThreatActor</Button>
          </div>
        </div>
        <Divider className="divider" />
        <div className="col lg-4 col-md-4 col-sm-12 col-xs-12 search">
          <TextField
            variant="outlined"
            placeholder="Search"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="card-overflow">
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Threat Actor</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>First Seen</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingData ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress className="text-blueviolet" />
                    </TableCell>
                  </TableRow>
                ) : threatactors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <WarningIcon color="black" fontSize="small" />
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  threatactors.map((threatactor) => (
                    <React.Fragment key={threatactor.id}>
                      <TableRow>
                        <TableCell>
                          <div className="flex cell-items">
                            <div className="threatactor-container">
                              <img src="data:image/png;base64,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" alt="Threat Actor" className='w-75 h-75'></img>
                            </div>
                            <span>{threatactor.actor_name}</span>                            
                          </div>
                        </TableCell>
                        <TableCell className="text-secondary">{threatactor.geo_origin}</TableCell>
                        <TableCell>{threatactor.first_seen}</TableCell>
                        <TableCell className="table-email">{threatactor.last_seen}</TableCell>
                        <TableCell>
                          <Tooltip title="View Threat-Actor" placement="top">
                            <img src="../assets/viewicon.svg" alt="View" className='icons-style' onClick={() => handleViewCompany(threatactor)} />
                          </Tooltip>
                          <Tooltip title="Edit ThreatActor" placement="top">
                            <div className="edit-container" onClick={() => handleEditCompany(threatactor.id)}>
                              <EditIcon className='usersandediticon' />
                            </div>
                          </Tooltip>
                          <Tooltip title="Delete ThreatActor" placement="top">
                            <img src="../assets/deleteicon.svg" alt="Delete" className='icons-style' onClick={() => handleDeleteClick(threatactor.actor_name, threatactor.id)} />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
  
          <TablePagination
            rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
            component="div"
            count={total ? total : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle align="center">
          Are you sure you want to delete this ThreatActor?
          <IconButton edge="end" color="inherit" onClick={() => setDeleteDialogOpen(false)} >
            <CloseIcon />
          </IconButton>
          <Divider className="divider" />
        </DialogTitle>

        <DialogContent className="dialogBox">
          <div className="dived-card">
            <div className="threatactor-container">
              <img
               src="data:image/png;base64,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"
               alt="Threat Actor"
               className='w-100 h-100'
               >
              </img>
            </div>
            <div className="m-2">
              <h4>{threatactorToDelete.name}</h4>
            </div>
          </div>
        </DialogContent>
  
        <DialogActions className="m-3">
          <Button onClick={() => setDeleteDialogOpen(false)} variant="contained" className="btn-card">
            Cancel
          </Button>
          <Button onClick={() => handleDeleteConfirm(threatactorToDelete.id)} variant="contained" className="btn-card danger" >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewThreatActors;